import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

async function invokeEdgeFunction(body: Record<string, unknown>): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;

  const { data, error } = await supabase.functions.invoke('search-console', {
    body: { ...body, userId },
  });

  if (error) {
    // FunctionsHttpError contains the response body
    let { message } = error;
    try {
      const ctx = await (error as any).context?.json?.();
      if (ctx?.error) message = ctx.error;
    } catch {
      /* ignore */
    }
    console.error('[SEOx] Edge function error:', message);
    throw new Error(message);
  }

  return data;
}

export type SearchConsoleProperty = {
  siteUrl: string;
  permissionLevel: string;
};

export type SearchAnalyticsRow = {
  keys: string[];
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};

export type SearchAnalyticsResponse = {
  rows: SearchAnalyticsRow[];
  responseAggregationType: string;
};

export type PeriodMetrics = {
  totalClicks: number;
  totalImpressions: number;
  avgCtr: number;
  avgPosition: number;
};

export type SearchConsoleSummary = {
  current: PeriodMetrics;
  previous: PeriodMetrics;
  topQueries: SearchAnalyticsRow[];
  topPages: SearchAnalyticsRow[];
  dailyTrend: SearchAnalyticsRow[];
};

export type SeoOpportunity = {
  type: 'striking_distance' | 'low_ctr' | 'declining' | 'quick_win';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  metric: string;
  keyword?: string;
  page?: string;
  position?: number;
  impressions?: number;
  clicks?: number;
  ctr?: number;
  positionChange?: number;
  clickChange?: number;
};

export type SeoOpportunities = {
  strikingDistance: SeoOpportunity[];
  lowCtr: SeoOpportunity[];
  declining: SeoOpportunity[];
  quickWins: SeoOpportunity[];
  totalCount: number;
};

export type GoogleConnectionStatus = {
  isConnected: boolean;
  siteUrl: string | null;
  connectedAt: string | null;
  availableSites: SearchConsoleProperty[];
};

export type GSCKeywordRow = {
  keyword: string;
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};

/**
 * Filter out junk queries from GSC -- search operators, dev artifacts, code strings, etc.
 */
function isValidKeyword(q: string): boolean {
  const trimmed = q.trim();

  // Too short or too long
  if (trimmed.length < 2 || trimmed.length > 120) return false;

  // Contains search operators (site:, inurl:, intitle:, filetype:, etc.)
  if (/\b(site|inurl|intitle|intext|filetype|cache|related):/i.test(trimmed)) return false;

  // Starts with special characters / code artifacts
  if (/^["{@#$<>_\\(]/.test(trimmed)) return false;

  // Mostly non-alpha (code, hashes, encoded strings)
  const alphaChars = trimmed.replace(/[^a-zA-Z\s]/g, '').length;
  if (alphaChars / trimmed.length < 0.4) return false;

  // Contains code-like patterns
  if (/(__\w+__|console\.|document\.|window\.|function\s*\(|=>|<\/?\w+>|\{\{|\}\})/i.test(trimmed))
    return false;

  // Contains URLs or file paths
  if (/https?:\/\/|www\.|\.com\/|\.js|\.ts|\.css|\.php/i.test(trimmed)) return false;

  return true;
}

const searchConsoleService = {
  async connectGoogle(redirectPath = '/settings/integrations'): Promise<void> {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${redirectPath}`,
        scopes:
          'https://www.googleapis.com/auth/webmasters.readonly https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/adwords',
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
  },

  async getConnectionStatus(): Promise<GoogleConnectionStatus> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return { isConnected: false, siteUrl: null, connectedAt: null, availableSites: [] };
    }

    const orgId = getActiveOrgId();
    let query = supabase
      .from('seo_google_connections')
      .select('*')
      .eq('user_id', user.id);

    if (orgId) query = query.eq('organization_id', orgId);

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.warn('[SEOx] getConnectionStatus error:', error.message);
      return { isConnected: false, siteUrl: null, connectedAt: null, availableSites: [] };
    }

    if (!data) {
      return { isConnected: false, siteUrl: null, connectedAt: null, availableSites: [] };
    }

    // DB-only check -- no edge function call. Use getAvailableSites() separately when needed.
    return {
      isConnected: true,
      siteUrl: data.site_url ?? null,
      connectedAt: data.created_at ?? null,
      availableSites: [],
    };
  },

  async getAvailableSites(): Promise<SearchConsoleProperty[]> {
    try {
      const sitesData = await invokeEdgeFunction({ action: 'listSites' });
      return sitesData?.siteEntry ?? [];
    } catch {
      return [];
    }
  },

  async saveConnection(payload: {
    providerToken: string;
    providerRefreshToken: string;
    siteUrl: string;
  }): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const orgId = getActiveOrgId();
    const { error } = await supabase.from('seo_google_connections').upsert(
      {
        user_id: user.id,
        provider_token: payload.providerToken,
        provider_refresh_token: payload.providerRefreshToken,
        site_url: payload.siteUrl,
        updated_at: new Date().toISOString(),
        ...(orgId ? { organization_id: orgId } : {}),
      },
      { onConflict: 'user_id' }
    );

    if (error) throw error;
  },

  async updateSiteUrl(siteUrl: string): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('seo_google_connections')
      .update({ site_url: siteUrl, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);

    if (error) throw error;
  },

  async disconnect(): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.from('seo_google_connections').delete().eq('user_id', user.id);

    if (error) throw error;
  },

  async getKeywordGSCData(
    siteUrl: string,
    keyword: string,
    days = 90
  ): Promise<{
    dailyTrend: Array<{
      date: string;
      clicks: number;
      impressions: number;
      ctr: number;
      position: number;
    }>;
    rankingPages: Array<{
      page: string;
      clicks: number;
      impressions: number;
      ctr: number;
      position: number;
    }>;
    totals: { clicks: number; impressions: number; avgCtr: number; avgPosition: number };
  }> {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 3);
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - days);
    const fmt = (d: Date): string => d.toISOString().split('T')[0];

    const queryFilter = [
      {
        filters: [{ dimension: 'query', expression: keyword.toLowerCase(), operator: 'contains' }],
      },
    ];

    // Fetch daily trend + ranking pages in parallel
    const [dailyData, pageData] = await Promise.all([
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(startDate),
        endDate: fmt(endDate),
        dimensions: ['date'],
        dimensionFilterGroups: queryFilter,
        rowLimit: 120,
      }),
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(startDate),
        endDate: fmt(endDate),
        dimensions: ['page'],
        dimensionFilterGroups: queryFilter,
        rowLimit: 50,
      }),
    ]);

    const dailyRows: SearchAnalyticsRow[] = dailyData?.rows ?? [];
    const pageRows: SearchAnalyticsRow[] = pageData?.rows ?? [];

    const dailyTrend = dailyRows.map((r) => ({
      date: r.keys[0],
      clicks: r.clicks,
      impressions: r.impressions,
      ctr: r.ctr,
      position: r.position,
    }));

    const rankingPages = pageRows
      .sort((a, b) => b.clicks - a.clicks)
      .map((r) => ({
        page: r.keys[0],
        clicks: r.clicks,
        impressions: r.impressions,
        ctr: r.ctr,
        position: r.position,
      }));

    const totalClicks = dailyRows.reduce((s, r) => s + r.clicks, 0);
    const totalImpressions = dailyRows.reduce((s, r) => s + r.impressions, 0);

    return {
      dailyTrend,
      rankingPages,
      totals: {
        clicks: totalClicks,
        impressions: totalImpressions,
        avgCtr: totalImpressions > 0 ? totalClicks / totalImpressions : 0,
        avgPosition:
          dailyRows.length > 0
            ? dailyRows.reduce((s, r) => s + r.position * r.impressions, 0) /
              Math.max(totalImpressions, 1)
            : 0,
      },
    };
  },

  async getSiteList(): Promise<SearchConsoleProperty[]> {
    const data = await invokeEdgeFunction({ action: 'listSites' });
    return data?.siteEntry ?? [];
  },

  async getSearchAnalytics(params: {
    siteUrl: string;
    startDate: string;
    endDate: string;
    dimensions: string[];
    rowLimit?: number;
  }): Promise<SearchAnalyticsResponse> {
    const data = await invokeEdgeFunction({
      action: 'searchAnalytics',
      siteUrl: params.siteUrl,
      startDate: params.startDate,
      endDate: params.endDate,
      dimensions: params.dimensions,
      rowLimit: params.rowLimit ?? 10,
    });
    return data as SearchAnalyticsResponse;
  },

  async getKeywordsWithPages(siteUrl: string, days = 28, rowLimit = 500): Promise<GSCKeywordRow[]> {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 3); // GSC ~3 day lag
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - days);

    const fmt = (d: Date): string => d.toISOString().split('T')[0];

    const data = await invokeEdgeFunction({
      action: 'searchAnalytics',
      siteUrl,
      startDate: fmt(startDate),
      endDate: fmt(endDate),
      dimensions: ['query', 'page'],
      rowLimit,
    });

    const rows: SearchAnalyticsRow[] = data?.rows ?? [];

    // Group by query -- keep the page with the most clicks for each keyword
    const byQuery = new Map<string, GSCKeywordRow>();
    for (const row of rows) {
      const keyword = row.keys[0];
      const page = row.keys[1];

      // Skip junk queries
      if (!isValidKeyword(keyword)) continue;

      const existing = byQuery.get(keyword);
      if (!existing || row.clicks > existing.clicks) {
        byQuery.set(keyword, {
          keyword,
          page,
          clicks: row.clicks,
          impressions: row.impressions,
          ctr: row.ctr,
          position: row.position,
        });
      }
    }

    return Array.from(byQuery.values()).sort((a, b) => b.impressions - a.impressions);
  },

  async getSummary(siteUrl: string, days = 28): Promise<SearchConsoleSummary> {
    const curEnd = new Date();
    // GSC data has ~3 day lag
    curEnd.setDate(curEnd.getDate() - 3);
    const curStart = new Date(curEnd);
    curStart.setDate(curEnd.getDate() - days);
    const prevEnd = new Date(curStart);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevEnd.getDate() - days);

    const fmt = (d: Date): string => d.toISOString().split('T')[0];

    const [queriesData, pagesData, prevData, dailyData] = await Promise.all([
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(curStart),
        endDate: fmt(curEnd),
        dimensions: ['query'],
        rowLimit: 25,
      }),
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(curStart),
        endDate: fmt(curEnd),
        dimensions: ['page'],
        rowLimit: 15,
      }),
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(prevStart),
        endDate: fmt(prevEnd),
        dimensions: ['query'],
        rowLimit: 1,
      }),
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(curStart),
        endDate: fmt(curEnd),
        dimensions: ['date'],
        rowLimit: 60,
      }),
    ]);

    const queryRows: SearchAnalyticsRow[] = queriesData?.rows ?? [];
    const pageRows: SearchAnalyticsRow[] = pagesData?.rows ?? [];
    const prevRows: SearchAnalyticsRow[] = prevData?.rows ?? [];
    const dailyRows: SearchAnalyticsRow[] = dailyData?.rows ?? [];

    const calcMetrics = (rows: SearchAnalyticsRow[]): PeriodMetrics => {
      const totalClicks = rows.reduce((s, r) => s + r.clicks, 0);
      const totalImpressions = rows.reduce((s, r) => s + r.impressions, 0);
      return {
        totalClicks,
        totalImpressions,
        avgCtr: totalImpressions > 0 ? totalClicks / totalImpressions : 0,
        avgPosition:
          rows.length > 0
            ? rows.reduce((s, r) => s + r.position * r.impressions, 0) /
              Math.max(totalImpressions, 1)
            : 0,
      };
    };

    return {
      current: calcMetrics(dailyRows.length > 0 ? dailyRows : queryRows),
      previous: calcMetrics(prevRows),
      topQueries: queryRows,
      topPages: pageRows,
      dailyTrend: dailyRows,
    };
  },

  async getOpportunities(siteUrl: string, days = 28): Promise<SeoOpportunities> {
    const curEnd = new Date();
    curEnd.setDate(curEnd.getDate() - 3);
    const curStart = new Date(curEnd);
    curStart.setDate(curEnd.getDate() - days);
    const prevEnd = new Date(curStart);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevEnd.getDate() - days);

    const fmt = (d: Date): string => d.toISOString().split('T')[0];

    // Fetch wide datasets: all queries (current + previous) and all pages (current + previous)
    const [curQueries, prevQueries, curPages, prevPages] = await Promise.all([
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(curStart),
        endDate: fmt(curEnd),
        dimensions: ['query'],
        rowLimit: 500,
      }),
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(prevStart),
        endDate: fmt(prevEnd),
        dimensions: ['query'],
        rowLimit: 500,
      }),
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(curStart),
        endDate: fmt(curEnd),
        dimensions: ['page'],
        rowLimit: 200,
      }),
      invokeEdgeFunction({
        action: 'searchAnalytics',
        siteUrl,
        startDate: fmt(prevStart),
        endDate: fmt(prevEnd),
        dimensions: ['page'],
        rowLimit: 200,
      }),
    ]);

    const curQ: SearchAnalyticsRow[] = curQueries?.rows ?? [];
    const prevQ: SearchAnalyticsRow[] = prevQueries?.rows ?? [];
    const curP: SearchAnalyticsRow[] = curPages?.rows ?? [];
    const prevP: SearchAnalyticsRow[] = prevPages?.rows ?? [];

    const prevPMap = new Map(prevP.map((r) => [r.keys[0], r]));

    // Average CTR by position bucket (rough Google benchmarks)
    const expectedCtr = (pos: number): number => {
      if (pos <= 1) return 0.3;
      if (pos <= 2) return 0.15;
      if (pos <= 3) return 0.1;
      if (pos <= 5) return 0.05;
      if (pos <= 10) return 0.02;
      return 0.005;
    };

    // 1. Striking Distance: queries ranking 8-20 with 100+ impressions
    const strikingDistance: SeoOpportunity[] = curQ
      .filter((r) => r.position >= 8 && r.position <= 20 && r.impressions >= 100)
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 15)
      .map((r) => ({
        type: 'striking_distance' as const,
        priority: ((): 'high' | 'medium' | 'low' => {
          if (r.position <= 12) return 'high';
          if (r.position <= 15) return 'medium';
          return 'low';
        })(),
        title: r.keys[0],
        description: `Ranking #${r.position.toFixed(
          0
        )} with ${r.impressions.toLocaleString()} impressions. A content update could push this to page 1.`,
        metric: `#${r.position.toFixed(0)} avg position`,
        keyword: r.keys[0],
        position: r.position,
        impressions: r.impressions,
        clicks: r.clicks,
        ctr: r.ctr,
      }));

    // 2. Low CTR: queries with CTR below expected for their position
    const lowCtr: SeoOpportunity[] = curQ
      .filter(
        (r) => r.position <= 10 && r.impressions >= 50 && r.ctr < expectedCtr(r.position) * 0.6
      )
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 15)
      .map((r) => {
        const expected = expectedCtr(r.position);
        const potentialClicks = Math.round(r.impressions * expected) - r.clicks;
        return {
          type: 'low_ctr' as const,
          priority: ((): 'high' | 'medium' | 'low' => {
            if (potentialClicks > 50) return 'high';
            if (potentialClicks > 20) return 'medium';
            return 'low';
          })(),
          title: r.keys[0],
          description: `CTR is ${(r.ctr * 100).toFixed(1)}% vs ${(expected * 100).toFixed(
            0
          )}% expected for position #${r.position.toFixed(
            0
          )}. Improving title/meta could gain ~${potentialClicks} clicks/mo.`,
          metric: `+${potentialClicks} potential clicks`,
          keyword: r.keys[0],
          position: r.position,
          impressions: r.impressions,
          clicks: r.clicks,
          ctr: r.ctr,
        };
      });

    // 3. Declining: pages that lost significant clicks vs previous period
    const declining: SeoOpportunity[] = curP
      .filter((r) => {
        const prev = prevPMap.get(r.keys[0]);
        if (!prev) return false;
        return prev.clicks > 10 && r.clicks < prev.clicks * 0.7; // 30%+ drop
      })
      .map((r) => {
        const prev = prevPMap.get(r.keys[0])!;
        const change = r.clicks - prev.clicks;
        const pctDrop = ((change / prev.clicks) * 100).toFixed(0);
        return {
          type: 'declining' as const,
          priority: ((): 'high' | 'medium' | 'low' => {
            if (change < -50) return 'high';
            if (change < -20) return 'medium';
            return 'low';
          })(),
          title: r.keys[0].replace(/^https?:\/\/[^/]+/, '') || '/',
          description: `Down ${Math.abs(change)} clicks (${pctDrop}%) vs previous period. Was ${
            prev.clicks
          }, now ${r.clicks}.`,
          metric: `${pctDrop}% drop`,
          page: r.keys[0],
          clicks: r.clicks,
          clickChange: change,
          impressions: r.impressions,
        };
      })
      .sort((a, b) => (a.clickChange ?? 0) - (b.clickChange ?? 0))
      .slice(0, 10);

    // 4. Quick Wins: queries ranking 4-8 with decent impressions (almost on top positions)
    const quickWins: SeoOpportunity[] = curQ
      .filter((r) => r.position >= 4 && r.position <= 8 && r.impressions >= 200)
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 10)
      .map((r) => {
        const topCtr = expectedCtr(3);
        const potentialClicks = Math.round(r.impressions * topCtr) - r.clicks;
        return {
          type: 'quick_win' as const,
          priority: 'high' as const,
          title: r.keys[0],
          description: `Ranking #${r.position.toFixed(
            0
          )} with ${r.impressions.toLocaleString()} impressions. Moving to top 3 could gain ~${potentialClicks} clicks/mo.`,
          metric: `+${potentialClicks} potential clicks`,
          keyword: r.keys[0],
          position: r.position,
          impressions: r.impressions,
          clicks: r.clicks,
          ctr: r.ctr,
        };
      });

    return {
      strikingDistance,
      lowCtr,
      declining,
      quickWins,
      totalCount: strikingDistance.length + lowCtr.length + declining.length + quickWins.length,
    };
  },
};

export default searchConsoleService;
